type UnknownRecord = Record<string, unknown>

const toStringValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  return null
}

const toStringArray = (value: unknown): string[] => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value
      .map(toStringValue)
      .filter((item): item is string => Boolean(item && item.trim()))
      .map(item => item.trim())
  }
  const str = toStringValue(value)
  return str ? [str.trim()] : []
}

const parseFlowString = (flow: string): { source?: string; destination?: string } => {
  const parts = flow.split('->').map(part => part.trim()).filter(Boolean)
  if (parts.length >= 2) {
    return { source: parts[0], destination: parts[1] }
  }
  return {}
}

export interface ViolationFlow {
  source?: string
  destination?: string
  disposition?: string
  raw?: string
}

const collectCandidateLists = (violation: UnknownRecord): unknown[] => {
  const candidates: unknown[] = []

  const keys = [
    'sample_flows',
    'sampleFlows',
    'flows',
    'impacted_flows',
    'impactedPaths',
    'impacted_paths',
    'paths',
  ]

  keys.forEach((key) => {
    if (Array.isArray(violation[key] as unknown[])) {
      candidates.push(violation[key])
    }
  })

  const details = violation.details
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    keys.forEach((key) => {
      const nested = (details as UnknownRecord)[key]
      if (Array.isArray(nested)) {
        candidates.push(nested)
      }
    })
  }

  return candidates
}

export const extractViolationFlows = (violation: UnknownRecord): ViolationFlow[] => {
  const flows: ViolationFlow[] = []

  const candidates = collectCandidateLists(violation)
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue

    candidate.forEach((entry) => {
      if (!entry) return

      if (typeof entry === 'string') {
        const parsed = parseFlowString(entry)
        flows.push({
          source: parsed.source,
          destination: parsed.destination,
          raw: entry,
        })
        return
      }

      if (typeof entry === 'object') {
        const record = entry as UnknownRecord
        const flowField =
          toStringValue(record.Flow) ??
          toStringValue(record.flow) ??
          toStringValue(record.path) ??
          null

        let source =
          toStringValue(record.source) ??
          toStringValue(record.Source) ??
          toStringValue(record.src) ??
          toStringValue(record.origin) ??
          null

        let destination =
          toStringValue(record.destination) ??
          toStringValue(record.dest) ??
          toStringValue(record.target) ??
          toStringValue(record.Destination) ??
          toStringValue(record.dst) ??
          null

        if ((!source || !destination) && flowField) {
          const parsed = parseFlowString(flowField)
          source = source ?? parsed.source ?? null
          destination = destination ?? parsed.destination ?? null
        }

        const disposition =
          toStringValue(record.Flow_Disposition) ??
          toStringValue(record.disposition) ??
          toStringValue(record.result) ??
          toStringValue(record.reason) ??
          null

        flows.push({
          source: source ?? undefined,
          destination: destination ?? undefined,
          disposition: disposition ?? undefined,
          raw: flowField ?? undefined,
        })
      }
    })
  }

  if (flows.length > 0) {
    return flows.filter(
      ({ source, destination, raw }) => Boolean((source && destination) || raw)
    )
  }

  const sources = [
    ...toStringArray(violation.source),
    ...toStringArray((violation as UnknownRecord).sources),
  ]
  const destinations = [
    ...toStringArray(violation.destination),
    ...toStringArray((violation as UnknownRecord).destinations),
  ]
  const dispositions = [
    ...toStringArray((violation as UnknownRecord).disposition),
    ...toStringArray((violation as UnknownRecord).dispositions),
  ]

  if (sources.length && destinations.length && sources.length === destinations.length) {
    return sources.map((source, index) => ({
      source,
      destination: destinations[index],
      disposition: dispositions[index],
    }))
  }

  return []
}
